// Pre-cache OpenStreetMap tiles for a canvasser's assigned turf so the map works
// with zero connectivity at the door.
//
// NOTE on tile usage: bulk-fetching from tile.openstreetmap.org violates OSM's
// tile policy at scale. For production, point TILE_URL_TEMPLATE at your own tile
// server or a provider (MapTiler/Stadia). The default below is fine for a small
// turf warm-up during development.

import type { GeoBounds } from '../db/types';

export const TILE_URL_TEMPLATE =
  (import.meta.env.VITE_TILE_URL as string | undefined) ??
  'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

/** Cache bucket name — MUST match the runtimeCaching cacheName in vite.config.ts. */
export const TILE_CACHE = 'osm-tiles';

export interface Bbox {
  west: number;
  south: number;
  east: number;
  north: number;
}

/** Reduce a GeoBounds (bbox or polygon) to a bounding box. */
export function bboxFromBounds(bounds: GeoBounds): Bbox | null {
  if (bounds.bbox) {
    const [west, south, east, north] = bounds.bbox;
    return { west, south, east, north };
  }
  if (bounds.polygon?.length) {
    let west = Infinity;
    let south = Infinity;
    let east = -Infinity;
    let north = -Infinity;
    for (const ring of bounds.polygon) {
      for (const [lng, lat] of ring) {
        west = Math.min(west, lng);
        east = Math.max(east, lng);
        south = Math.min(south, lat);
        north = Math.max(north, lat);
      }
    }
    if (Number.isFinite(west)) return { west, south, east, north };
  }
  // GeoJSON geometry ({type:'Polygon'|'MultiPolygon', coordinates}) — the shape the
  // City of Victoria neighbourhood import stores. Walk all coordinates for the bbox.
  const coords = (bounds as { coordinates?: unknown }).coordinates;
  if (coords) {
    let west = Infinity;
    let south = Infinity;
    let east = -Infinity;
    let north = -Infinity;
    const walk = (a: unknown): void => {
      if (!Array.isArray(a)) return;
      if (typeof a[0] === 'number') {
        const lng = a[0] as number;
        const lat = a[1] as number;
        west = Math.min(west, lng);
        east = Math.max(east, lng);
        south = Math.min(south, lat);
        north = Math.max(north, lat);
      } else for (const c of a) walk(c);
    };
    walk(coords);
    if (Number.isFinite(west)) return { west, south, east, north };
  }
  return null;
}

export function lngToTileX(lng: number, z: number): number {
  return Math.floor(((lng + 180) / 360) * 2 ** z);
}

export function latToTileY(lat: number, z: number): number {
  const rad = (lat * Math.PI) / 180;
  return Math.floor(((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * 2 ** z);
}

export interface TileCoord {
  z: number;
  x: number;
  y: number;
}

/** Enumerate tile coords covering a bbox across a zoom range (inclusive). */
export function tilesForBbox(bbox: Bbox, minZoom: number, maxZoom: number, maxTiles = 1500): TileCoord[] {
  const tiles: TileCoord[] = [];
  for (let z = minZoom; z <= maxZoom; z += 1) {
    const x0 = lngToTileX(bbox.west, z);
    const x1 = lngToTileX(bbox.east, z);
    const y0 = latToTileY(bbox.north, z); // north has the smaller y
    const y1 = latToTileY(bbox.south, z);
    for (let x = Math.min(x0, x1); x <= Math.max(x0, x1); x += 1) {
      for (let y = Math.min(y0, y1); y <= Math.max(y0, y1); y += 1) {
        tiles.push({ z, x, y });
        if (tiles.length >= maxTiles) return tiles; // hard cap — never runaway
      }
    }
  }
  return tiles;
}

export function tileUrl({ z, x, y }: TileCoord): string {
  return TILE_URL_TEMPLATE.replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y));
}

export interface PrecacheResult {
  requested: number;
  cached: number;
  skipped: number;
  capped: boolean;
}

/**
 * Warm the tile cache for a turf. Writes into the same Cache Storage bucket the
 * service worker serves from, so cached tiles render offline.
 */
export async function precacheTurfTiles(
  bounds: GeoBounds,
  opts: { minZoom?: number; maxZoom?: number; maxTiles?: number } = {},
): Promise<PrecacheResult> {
  const { minZoom = 15, maxZoom = 17, maxTiles = 1500 } = opts;
  const bbox = bboxFromBounds(bounds);
  if (!bbox || typeof caches === 'undefined') {
    return { requested: 0, cached: 0, skipped: 0, capped: false };
  }

  const tiles = tilesForBbox(bbox, minZoom, maxZoom, maxTiles);
  const capped = tiles.length >= maxTiles;
  const cache = await caches.open(TILE_CACHE);

  let cached = 0;
  let skipped = 0;
  for (const t of tiles) {
    const url = tileUrl(t);
    if (await cache.match(url)) {
      skipped += 1;
      continue;
    }
    try {
      const res = await fetch(url, { mode: 'cors' });
      if (res.ok) {
        await cache.put(url, res.clone());
        cached += 1;
      }
    } catch {
      // offline / blocked — leave it; runtime caching will fill it on next view
    }
  }
  return { requested: tiles.length, cached, skipped, capped };
}
